import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Play, Pause, FileText, UserPlus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useWaveform } from '@/lib/hooks/useWaveform';
import { MOCK_CALLS, MOCK_CALL_DETAILS, STATUS_CONFIG } from '@/data/mock';
import type { ExtractedInfo, TranscriptLine } from '@/data/mock';

export const Route = createFileRoute('/klici/$id')({
  component: KlicDetailPage,
});

function KlicDetailPage() {
  const { id } = Route.useParams();
  const call   = MOCK_CALLS.find(c => String(c.id) === id);
  const detail = call !== undefined ? MOCK_CALL_DETAILS[call.id] : undefined;

  if (call === undefined || detail === undefined) {
    return (
      <AppShell pageTitle="Klic ni najden" crumb="Klici · —">
        <p style={{ color: 'var(--app-muted)', fontSize: '0.9rem' }}>
          Klic z ID <code>{id}</code> ne obstaja.
        </p>
        <Link
          to="/klici"
          style={{ color: 'var(--copper-300)', fontSize: '0.86rem', marginTop: '0.75rem', display: 'inline-block' }}
        >
          ← Nazaj na klice
        </Link>
      </AppShell>
    );
  }

  const { cls, label } = STATUS_CONFIG[call.status];

  return (
    <AppShell pageTitle={call.who} crumb="Klici · Podrobnosti">
      {/* Back + meta bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        marginBottom: '1.1rem',
        flexWrap: 'wrap',
      }}>
        <Link
          to="/klici"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: 'var(--app-muted)',
            fontSize: '0.82rem',
            fontFamily: 'var(--font-mono)',
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={13} />
          Nazaj
        </Link>
        <span style={{ color: 'var(--app-line2)', userSelect: 'none' }}>|</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--app-subtle)' }}>
          {call.date} · {call.time}
        </span>
        <span className={`pill ${cls} pill--dot`}>{label}</span>
      </div>

      {/* Audio player */}
      <AudioPlayer dur={call.dur} />

      {/* 2-col detail grid */}
      <div className="cd-grid">
        {/* Transcript */}
        <div className="app-card" style={{ padding: '1.25rem' }}>
          <div className="card-head" style={{ marginBottom: '1rem' }}>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 540,
              fontSize: '1rem',
              color: '#fff',
              margin: 0,
            }}>
              Prepis pogovora
            </h3>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--app-subtle)' }}>
              {detail.transcript.length} vrstic
            </span>
          </div>

          {detail.transcript.length > 0 ? (
            <div className="transcript scroll-thin">
              {detail.transcript.map((line, i) => (
                <TranscriptRow key={i} line={line} />
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--app-subtle)', fontSize: '0.84rem', fontStyle: 'italic', margin: 0 }}>
              Prepis ni na voljo za ta klic.
            </p>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* AI summary */}
          <div className="app-card" style={{ padding: '1.25rem' }}>
            <div className="card-head" style={{ marginBottom: '0.85rem' }}>
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 540,
                fontSize: '1rem',
                color: '#fff',
                margin: 0,
              }}>
                Povzetek AI
              </h3>
            </div>
            <p className="summary-block">{detail.summary}</p>

            <ExtractSection extracted={detail.extracted} />
          </div>

          {/* Actions */}
          <div className="app-card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              width: '100%',
              padding: '0.72rem 1rem',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--copper-500)',
              color: '#fff',
              border: '0',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: '0.88rem',
              fontWeight: 540,
            }}>
              <FileText size={15} strokeWidth={1.8} />
              Ustvari ponudbo
            </button>
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              width: '100%',
              padding: '0.72rem 1rem',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--app-raised2)',
              color: 'var(--app-text)',
              border: '1px solid var(--app-line2)',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: '0.88rem',
              fontWeight: 540,
            }}>
              <UserPlus size={15} strokeWidth={1.8} />
              Dodaj v kontakte
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function AudioPlayer({ dur }: { dur: string }) {
  const { canvasRef, ctrlRef } = useWaveform({ amp: 0.34, grid: false, autostart: false });
  const [playing, setPlaying] = useState(false);

  function togglePlay() {
    const next = !playing;
    if (next) {
      ctrlRef.current?.start();
    } else {
      ctrlRef.current?.stop();
    }
    setPlaying(next);
  }

  return (
    <div className="cd-player">
      <button
        onClick={togglePlay}
        className="cd-play"
        aria-label={playing ? 'Pavza' : 'Predvajaj'}
      >
        {playing
          ? <Pause size={15} strokeWidth={2} />
          : <Play  size={15} strokeWidth={2} />
        }
      </button>
      <canvas ref={canvasRef} className="cd-wave-mini" />
      <span className="cd-dur">{dur}</span>
    </div>
  );
}

function TranscriptRow({ line }: { line: TranscriptLine }) {
  return (
    <div className={`tline tline--${line.who}`}>
      <span className="tline__who">
        {line.who === 'agent' ? 'Agent' : 'Stranka'}
      </span>
      <span className="tline__text">{line.text}</span>
      <span className="tline__t">{line.t}</span>
    </div>
  );
}

function ExtractSection({ extracted }: { extracted: ExtractedInfo }) {
  const rows = [
    { label: 'Ime',     value: extracted.person },
    { label: 'Naslov',  value: extracted.address },
    { label: 'Termin',  value: extracted.appointment },
    { label: 'Telefon', value: extracted.phone },
  ].filter((r): r is { label: string; value: string } => r.value !== undefined);

  if (rows.length === 0) return null;

  return (
    <div style={{ marginTop: '1rem' }}>
      <div className="klabel" style={{ marginBottom: '0.4rem' }}>Zaznano iz pogovora</div>
      {rows.map(({ label, value }) => (
        <div key={label} className="extract-row">
          <span className="klabel" style={{ width: '56px', flexShrink: 0, paddingTop: '0.05rem' }}>
            {label}
          </span>
          <span style={{ fontSize: '0.86rem', color: 'var(--app-text)' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}
